import { MapEditorContextMenu } from "@gettactic/map-builder/src/components/map/editor/context-menu/MapEditorContextMenu";
import { EditorLayout } from "@gettactic/map-builder/src/components/map/editor/EditorLayout/EditorLayout";
import { PanelLayoutProvider } from "@gettactic/map-builder/src/components/map/editor/EditorLayout/EditorLayoutContext";
import { useGlobalEditorShortcuts } from "@gettactic/map-builder/src/components/map/editor/shortcuts/hooks/useGlobalEditorShortcuts";
import { useShapeSelectionShortcuts } from "@gettactic/map-builder/src/components/map/editor/shortcuts/hooks/useShapeSelectionShortcuts";
import { EditorToolbars } from "@gettactic/map-builder/src/components/map/editor/toolbars/EditorToolbars";
import { GridPrompt } from "@gettactic/map-builder/src/components/map/editor/toolbars/GridPrompt";
import { MeasurementPrompt } from "@gettactic/map-builder/src/components/map/editor/toolbars/MeasurementPrompt";
import { TopToolbar } from "@gettactic/map-builder/src/components/map/editor/toolbars/TopToolbar";
import React from "react";
import { useCanvasGestures } from "../gesture/useCanvasGestures";
import { useDuplicateShortcuts } from "../gesture/useDuplicateShortcuts";
import { useWallShortcuts } from "../gesture/useWallShortcuts";
import { AutoZonePickerCard } from "../panels/AutoZonePickerCard";
import { useFitOnMount, ZoomPill } from "../panels/ZoomPill";
import { MapSvg } from "../render/MapSvg";
import { useOverlayChannel } from "../render/overlayChannel";
import { useEditorSelector, useEditorStore } from "../store/EditorStoreContext";
import { buildingFootprint, roomPolygonsOf } from "../tools/autoZoneCreate";
import { applyGridConfig } from "../tools/gridTool";
import type { Tool } from "../tools/Tool";
import type { ToolKind } from "../types";
import { PreviewOverlay } from "./PreviewOverlay";
import { ShapeMetricsProvider } from "./ShapeMetricsProvider";

export interface EditorChromeProps {
  tools: Partial<Record<ToolKind, Tool>>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  svgRef: React.RefObject<SVGSVGElement | null>;
}

/** The measure and grid tools finish in a small form over the canvas. */
const ToolPrompts: React.FC = () => {
  const store = useEditorStore();
  const pendingMeasurement = useEditorSelector(
    (state) => state.chrome.pendingMeasurement
  );
  const pendingGrid = useEditorSelector((state) => state.chrome.pendingGrid);
  const pixelsPerMeter = useEditorSelector(
    (state) => state.meta.pixelsPerMeter ?? null
  );
  // The enclosed floor area, for calibrating from the building's m².
  const footprint = React.useMemo(
    () =>
      pendingMeasurement
        ? buildingFootprint(roomPolygonsOf(store.getState()))
        : null,
    [pendingMeasurement, store]
  );
  const closePrompt = () =>
    store.getState().setChrome({ pendingMeasurement: null });
  return (
    <>
      {pendingMeasurement ? (
        <MeasurementPrompt
          pixelLength={pendingMeasurement.pixelLength}
          pixelsPerMeter={pixelsPerMeter}
          footprint={footprint}
          onConfirm={(meters) => {
            const length = pendingMeasurement.pixelLength;
            if (length && Number.isFinite(meters) && meters > 0) {
              store.getState().setMeta({ pixelsPerMeter: length / meters });
            }
            closePrompt();
          }}
          onConfirmArea={(squareMeters) => {
            if (footprint && squareMeters > 0) {
              store.getState().setMeta({
                pixelsPerMeter: Math.sqrt(footprint.areaPx / squareMeters)
              });
            }
            closePrompt();
          }}
          onCancel={closePrompt}
        />
      ) : null}
      {pendingGrid ? (
        <GridPrompt
          rect={{ width: pendingGrid.width, height: pendingGrid.height }}
          onConfirm={(config) => applyGridConfig(store, pendingGrid, config)}
          onCancel={() => store.getState().setChrome({ pendingGrid: null })}
        />
      ) : null}
    </>
  );
};

/** Shortcut hooks isolated so their subscriptions never re-render the frame. */
const EditorShortcuts: React.FC = () => {
  useGlobalEditorShortcuts();
  useShapeSelectionShortcuts();
  useWallShortcuts();
  useDuplicateShortcuts();
  return null;
};

/**
 * The Konva editor's frame (top toolbar, side panels, tools dock, floating
 * toolboxes) around the SVG canvas. Same components, same layout; only the
 * canvas and the zoom pill are new.
 */
export const EditorChrome: React.FC<EditorChromeProps> = ({
  tools,
  containerRef,
  svgRef
}) => {
  const store = useEditorStore();
  const overlay = useOverlayChannel();
  const rootRef = React.useRef<SVGGElement | null>(null);

  useCanvasGestures({ store, overlay, tools, svgRef, rootRef, containerRef });
  useFitOnMount(containerRef);

  return (
    <ShapeMetricsProvider svgRef={svgRef} containerRef={containerRef}>
      <PanelLayoutProvider>
        <EditorShortcuts />
        <div className="h-full w-full bg-v2-background p-v2-layout-card-out">
          <div className="relative flex h-full w-full flex-col overflow-hidden rounded-lg border border-neutral-200 bg-v2-card shadow-componentCard">
            <div className="flex h-12 w-full flex-none">
              <TopToolbar />
            </div>
            <div className="min-h-0 w-full flex-1">
              <EditorLayout>
                <MapEditorContextMenu>
                  <div
                    ref={containerRef}
                    role="application"
                    aria-label="Map canvas"
                    // biome-ignore lint/a11y/noNoninteractiveTabindex: the canvas takes keyboard shortcuts
                    tabIndex={0}
                    className="relative h-full w-full outline-none"
                    data-map-editor
                  >
                    <MapSvg svgRef={svgRef} rootRef={rootRef} />
                    <AutoZonePickerCard />
                    <ToolPrompts />
                    <PreviewOverlay />
                  </div>
                </MapEditorContextMenu>
                <ZoomPill canvasRef={containerRef} />
              </EditorLayout>
            </div>
          </div>
        </div>
        <EditorToolbars />
      </PanelLayoutProvider>
    </ShapeMetricsProvider>
  );
};
